import React from 'react';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import AutocompleteInput from 'src/components/UI/input/AutocompleteInput';
import { Grid } from '@mui/material';
import { SnackContext } from 'src/store/ContextStore';
import { getColor } from 'src/util/util';
import { renderTagChips } from './tagChips';
import AnonymousFreeTextBanner from '../../AnonymousFreeTextBanner';
import { memberIdentityOf } from '../memberIdentity';
import { withPersonTags } from '../personTagRows';
import axios from 'src/axios';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// Person-level tags: labels on an individual member (the selected node), stored
// on the working models (UserChanged for the proband, FamilyChanged for other
// members). Mirrors the family-level Tags.jsx and shares the org palette
// (Client.clientTags) fetched via GET /pedigree/tags.

const isSystemTag = (tag) => {
  const lower = (tag?.label ?? tag)?.toLowerCase() ?? '';
  return lower.startsWith('imported from') || lower.includes('merge');
};

// The labels the editor shows for a member's stored tags. System tags are
// neither offered nor posted, so they are left out here too.
export const editableTagLabels = (personTags) =>
  (Array.isArray(personTags) ? personTags : [])
    .map((t) => t?.tag)
    .filter((t) => t && !isSystemTag(t));

const sameLabels = (a, b) =>
  a.length === b.length && a.every((label, i) => label === b[i]);

// THE SECTION MUST SHOW THE SELECTED PERSON'S TAGS.
//
// Every sidebar section stays mounted across a change of person, so this
// component is not remounted when someone else is selected. It used to read
// the tags once, on mount, and never again: person B's Tags section showed
// person A's, and a Save from it wrote A's tags, plus the edit, onto B. Nothing
// had to be dirty for that to happen.
//
// So the editor follows the member's row in memberRecords, matched on
// memberIdentityOf (`_id`, never the pid), and re-seeds whenever the person or
// their stored tags change. The row rather than `data`: `data` is a click-time
// snapshot that nothing refreshes when only the tags change.
//
// AND A SAVE HAS TO REACH THAT ROW. The tags are written by their own endpoint,
// POST /pedigree/person-tags, which never touched memberRecords. That left two
// things reading the pre-save tags: this editor on the way back to the person,
// and PedigreeChart's autosave, whose POST /pedigree-config $sets every
// non-proband member's WHOLE row, personTags included. The next unrelated edit
// in the family therefore wrote the old tags back over the saved ones. The row
// is now updated before the request, without arming a pedigree save, and
// rolled back if the save is refused: see personTagRows.js, which every writer
// to that endpoint shares.
//
// Unsaved tag edits are left alone while the same person's row refreshes, and
// they are what marks the section dirty, so the sidebar's selection guard asks
// before a change of person discards them.
function PersonTags({
  data,
  memberRecords,
  setMemberRecords,
  user,
  onDirtyChange,
  saveRef,
  discardRef
}) {
  const t = useAutoTranslation();

  const { setSnack } = React.useContext(SnackContext);

  const identity = memberIdentityOf(data);
  const row = React.useMemo(
    () =>
      (identity != null &&
        (memberRecords || []).find((m) => memberIdentityOf(m) === identity)) ||
      data,
    [memberRecords, identity, data]
  );
  const storedTags = row?.personTags;
  const loadedTags = React.useMemo(
    () => editableTagLabels(storedTags),
    [storedTags]
  );
  const loadedKey = loadedTags.join('\n');

  const familyMemberId = data?.familyMemberId ?? data?.proBandId;
  const isProband = !!data?.proBandId;
  const user_id = user?.user_id || user?._id;

  const [tags, setTags] = React.useState(loadedTags);
  const [edited, setEdited] = React.useState(false);

  // Re-seed DURING RENDER, not in an effect. An effect would commit one render
  // holding the new person's identity with the previous person's tags, and the
  // save handler is refreshed from each committed render: a Save landing in
  // that window would post A's tags against B.
  const [synced, setSynced] = React.useState({ identity, loadedKey });
  if (synced.identity !== identity || synced.loadedKey !== loadedKey) {
    const samePerson = synced.identity === identity;
    setSynced({ identity, loadedKey });
    // The same person's row refreshing under an unsaved edit keeps the edit.
    // Anyone else, or an unedited editor, takes what is stored.
    if (!samePerson || !edited) {
      setTags(loadedTags);
      setEdited(false);
    }
  }

  // Read by a save that resolves after the clinician has moved on.
  const latestRef = React.useRef(null);
  latestRef.current = { identity, tags };

  React.useEffect(() => {
    onDirtyChange?.(edited);
  }, [edited]); // eslint-disable-line react-hooks/exhaustive-deps

  const [options, setOptions] = React.useState(
    CONSTANTS.tags.filter((t) => !isSystemTag(t))
  );

  React.useEffect(() => {
    axios.get(APICONSTANTS.setTags).then(({ data: { clientTags } }) => {
      if (clientTags.length > 0) {
        setOptions(clientTags.filter((t) => !isSystemTag(t)));
      }
    });
  }, []);

  const handleTags = (value) => {
    const next = [];
    value.map((tag) => {
      if (typeof tag === 'string' && !next.includes(tag)) next.push(tag);
      else {
        const label = tag?.inputValue || tag?.label;
        label && !next.includes(label) && next.push(label);
      }
    });
    setTags(next);
    setEdited(true);
  };

  const onSave = async () => {
    const savedIdentity = identity;
    const savedLabels = tags;
    const previousTags = storedTags;
    const clientTags = [];
    const personTags = [];
    tags.forEach((tag) => {
      const tagColor =
        options.find((t) => t.label === tag)?.tagColor ?? getColor(tag);
      clientTags.push({ label: tag, tagColor });
      personTags.push({ tag, tagColor });
    });

    setMemberRecords?.((prev) =>
      withPersonTags(prev, savedIdentity, personTags)
    );

    await axios
      .post(APICONSTANTS.personTags, {
        user_id,
        familyMemberId,
        isProband,
        clientTags,
        personTags
      })
      .then(() => {
        // Clean only if the editor still shows what was saved, for the person
        // it was saved for. A save that resolves after the clinician has moved
        // on, or has edited again, must not wipe that newer state's flag.
        const latest = latestRef.current;
        if (
          latest.identity === savedIdentity &&
          sameLabels(latest.tags, savedLabels)
        ) {
          setEdited(false);
        }
        setSnack({ status: true, msg: t('Tags Updated'), severity: 'success' });
      })
      .catch(() => {
        setMemberRecords?.((prev) =>
          withPersonTags(prev, savedIdentity, previousTags, personTags)
        );
        setSnack({
          status: true,
          msg: t('An error occured'),
          severity: 'error'
        });
      });
  };

  const onDiscard = () => {
    setTags(loadedTags);
    setEdited(false);
  };

  React.useEffect(() => {
    if (saveRef) saveRef.current = onSave;
    if (discardRef) discardRef.current = onDiscard;
  });

  return (
    <>
      <AnonymousFreeTextBanner sx={{ mb: 1.5 }} />
      <Grid container spacing={2}>
        <Grid size={12}>
          <AutocompleteInput
            multiple
            options={options}
            getOptionLabel={(option) => option}
            value={tags}
            onChange={(v) => handleTags(v)}
            renderValue={renderTagChips(options)}
          />
        </Grid>
      </Grid>
    </>
  );
}

export default PersonTags;
